import React, { Fragment, Component } from 'react';
import {
    Grid,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import { Launch } from '@mui/icons-material';
import SvgIcon from '@mui/material/SvgIcon';
import GetAppIcon from '@mui/icons-material/GetApp';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import HotelIcon from '@mui/icons-material/Hotel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import moment from 'moment';
import { ReactComponent as BestWesternIcon } from '/public/travelicons/hotelicons/BestWestern.svg';
import { ReactComponent as CandlewoodSuiteIcon } from '/public/travelicons/hotelicons/CandlewoodSuite.svg';
import { ReactComponent as CrownPlazaIcon } from '/public/travelicons/hotelicons/CrownPlaza.svg';
import { ReactComponent as HiltonIcon } from '/public/travelicons/hotelicons/Hilton.svg';
import { ReactComponent as HolidayInnIcon } from '/public/travelicons/hotelicons/HolidayInn.svg';
import { ReactComponent as IntercontinentalIcon } from '/public/travelicons/hotelicons/Intercontinental.svg';
import { ReactComponent as MarriottIcon } from '/public/travelicons/hotelicons/Marriott.svg';
import { ReactComponent as StaybridgeIcon } from '/public/travelicons/hotelicons/Staybridge.svg';
import { ReactComponent as GenericHotelIcon } from '/public/travelicons/hotelicons/GenericHotel.svg';
import { COLORS } from '../../../../utils/Application_Constants';
import { getTravelItinerary } from '../../../../services/AssignmentsService';

const styles = (theme) => ({
    AccordionSummaryRoot: {
        padding: '8px 20px',
    },
    title: {
        fontWeight: 'bold',
        fontSize: '16px',
        marginBottom: '4px',
        color: COLORS.RAVEN,
    },
    value: {
        fontSize: '14px',
        color: COLORS.RAVEN,
    },
    clickableLink: {
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    topRightIcon: {
        position: 'absolute',
        top: '8px',
        right: '16px',
    },
    flightCard: {
        marginTop: '5px',
        backgroundColor: 'white',
        padding: '16px',
        minWidth: '100%',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        position: 'relative',
    },
    iconClass: {
        verticalAlign: 'middle',
        color: COLORS.TRUE_BLUE,
        fontSize: '36px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '24px',
        marginTop: '8px',
        marginLeft: '16px',
        flexWrap: 'wrap',
    },
    buttonIcon: {
        marginRight: '8px',
        fontSize: '1.2rem',
        color: COLORS.TRUE_BLUE,
        backgroundColor: COLORS.LIGHT_GRAY,
        borderRadius: '50%',
        padding: '4px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        verticalAlign: 'middle',
    },
    button: {
        background: 'none',
        border: 'none',
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        padding: '0',
        font: 'inherit',
        display: 'flex',
        alignItems: 'center',
        minWidth: 120,
        whiteSpace: 'nowrap',
    },
    disabledButton: {
        color: 'gray',
        fontWeight: '500',
        fontSize: '14px',
        cursor: 'not-allowed',
        textDecoration: 'none',
        pointerEvents: 'none',
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexDirection: 'row',
    },
    '@media (max-width: 600px)': {
        button: {
            minWidth: 'auto',
        },
        buttonContent: {
            flexDirection: 'row',
        },
        buttonContainer: {
            justifyContent: 'flex-start',
        },
    },
    section: {
        marginBottom: '12px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    dialogPaperRoot: {
        padding: '40px',
        gap: '16px',
    },
    dialogContentRoot: {
        padding: '0',
        '&:first-child': {
            paddingTop: '0',
        },
    },
    dialogActionsRoot: {
        padding: '0',
    },
});

class LodgingV3 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            cancelModalOpen: false,
        };
    }

    mapsSelectorForTravel = (addressInfo) => {
        if (
            navigator.platform.indexOf('iPhone') !== -1 ||
            navigator.platform.indexOf('iPad') !== -1 ||
            navigator.platform.indexOf('iPod') !== -1
        ) {
            return `https://maps.apple.com/?q=${this.hotelAddressParser(addressInfo)}}`;
        }
        return `https://google.com/maps/search/?api=1&query=${this.hotelAddressParser(
            addressInfo
        )}`;
    };

    hotelAddressParser = (addressInfo) => {
        let encodedAddress = '';
        const addressString = `${addressInfo.propertyAddress1} ${addressInfo.propertyCity} ${addressInfo.propertyState} ${addressInfo.propertyZip}`;
        encodedAddress = encodeURI(addressString);
        return encodedAddress;
    };

    renderIcon = (vendorName) => {
        const { classes } = this.props;
        switch (vendorName) {
            case 'Best Western':
                return (
                    <SvgIcon
                        aria-labelledby="bestWesternTitleID"
                        component={BestWesternIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Candlewood Suites and Inns':
                return (
                    <SvgIcon
                        aria-labelledby="candlewoodTitleID"
                        component={CandlewoodSuiteIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Crowne Plaza':
                return (
                    <SvgIcon
                        aria-labelledby="crowneTitleID"
                        component={CrownPlazaIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Hilton Garden Inn':
            case 'Hilton Hotels Usa':
                return (
                    <SvgIcon
                        aria-labelledby="hiltonTitleID"
                        component={HiltonIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Holiday Inn':
                return (
                    <SvgIcon
                        aria-labelledby="holidayInnTitleID"
                        component={HolidayInnIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Intercontinental':
                return (
                    <SvgIcon
                        aria-labelledby="intercontinentalTitleID"
                        component={IntercontinentalIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Marriott Conference Centers':
            case 'Marriott Vacation Club Intl':
            case 'Marriott Hotels':
                return (
                    <SvgIcon
                        aria-labelledby="marriotTitleID"
                        component={MarriottIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            case 'Staybridge Sts by Holiday Inn':
                return (
                    <SvgIcon
                        aria-labelledby="staybridgeTitleID"
                        component={StaybridgeIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
                break;
            default:
                return (
                    <SvgIcon
                        aria-labelledby="genericHotelTitleID"
                        component={GenericHotelIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
        }
    };

    renderErrorDialog() {
        const { cancelModalOpen } = this.state;
        const { classes } = this.props;

        return (
            <Dialog
                open={cancelModalOpen}
                onClose={() => this.setState({ cancelModalOpen: false })}
                aria-labelledby="error-modal-title"
                aria-describedby="error-modal-description"
                classes={{ paper: classes.dialogPaperRoot }}
                fullWidth
                maxWidth="xs"
            >
                <DialogContent
                    id="error-modal-description"
                    classes={{ root: classes.dialogContentRoot }}
                >
                    The travel document is not currently available. For immediate assistance, please
                    contact your Clinician Relations Specialist.
                </DialogContent>
                <DialogActions classes={{ root: classes.dialogActionsRoot }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.setState({ cancelModalOpen: false })}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    render() {
        const { classes, item, externalId, assignmentId } = this.props;
        const { isLoading } = this.state;
        const isItineraryAvailable = !!item.eItinLink;
        const isCompletedOver30Days =
            item.completedDate &&
            moment(item.completedDate).isBefore(moment().subtract(30, 'days'));

        return (
            <Fragment key={`${item.tripKey}-${item.flightNumber}`}>
                {this.renderErrorDialog()}
                <Grid container direction="column" className={classes.flightCard}>
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>LODGING</div>
                                <div className={classes.value}>{item.vendorName}</div>
                            </div>
                            <div className={classes.section}>
                                <div className={classes.title}>ADDRESS</div>
                                <div
                                    className={classes.value}
                                >{`${item.propertyAddress1}, ${item.propertyCity}, ${item.propertyState} ${item.propertyZip}`}</div>
                            </div>
                        </div>
                        <div className={classes.topRightIcon}>
                            {this.renderIcon(item.vendorName)}
                        </div>
                    </div>
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>CHECK-IN</div>
                                <div className={classes.value}>{`${moment(
                                    item.originatedDate
                                ).format('MM/DD/YYYY')}`}</div>
                            </div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>CHECK-OUT</div>
                                <div className={classes.value}>{`${moment(
                                    item.completedDate
                                ).format('MM/DD/YYYY')}`}</div>
                            </div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>CONFIRMATION</div>
                                <div className={classes.value}>
                                    <InsertDriveFileOutlinedIcon fontSize="small" />
                                    {item.confirmationNumber}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.buttonContainer}>
                        <a
                            href={this.mapsSelectorForTravel(item)}
                            className={classes.clickableLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <LocationOnIcon className={classes.buttonIcon} />
                            Directions
                        </a>
                        {!isCompletedOver30Days && isItineraryAvailable ? (
                            <>
                                <a
                                    href={item.eItinLink}
                                    className={classes.clickableLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Launch className={classes.buttonIcon} />
                                    Itinerary
                                </a>
                                <button
                                    onClick={async () => {
                                        this.setState({ isLoading: true });
                                        try {
                                            await getTravelItinerary(
                                                externalId,
                                                assignmentId,
                                                item.recordLocator,
                                                moment(item.firstTravelDate).format('MM-DD-YYYY'),
                                                moment(item.lastTravelDate).format('MM-DD-YYYY')
                                            );
                                        } catch (error) {
                                            console.error('Error downloading file:', error);
                                            if (error && error.response.status === 404) {
                                                this.setState({ cancelModalOpen: true }); // Open the modal if it's a 404 error
                                            } else {
                                                alert(
                                                    'Failed to download the file. Please try again.'
                                                );
                                            }
                                        } finally {
                                            this.setState({ isLoading: false });
                                        }
                                    }}
                                    className={`${classes.button} ${
                                        isLoading ? classes.disabledButton : ''
                                    }`}
                                    disabled={isLoading}
                                    aria-label="Download Itinerary"
                                >
                                    <span className={classes.buttonContent}>
                                        {isLoading ? (
                                            <CircularProgress
                                                size={20}
                                                className={classes.buttonIcon}
                                            />
                                        ) : (
                                            <GetAppIcon className={classes.buttonIcon} />
                                        )}
                                        Download
                                    </span>
                                </button>
                            </>
                        ) : !isCompletedOver30Days ? (
                            <>
                                <Tooltip title="Booking information not available">
                                    <span>
                                        <a
                                            href="#"
                                            className={`${classes.clickableLink} ${classes.disabledButton}`}
                                            onClick={(e) => e.preventDefault()}
                                        >
                                            <Launch
                                                className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                            />
                                            Itinerary
                                        </a>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Travel information not available">
                                    <span className={classes.buttonContent}>
                                        <button
                                            className={`${classes.button} ${classes.disabledButton}`}
                                            disabled
                                            aria-label="Download Itinerary"
                                        >
                                            <GetAppIcon
                                                className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                            />
                                            Download
                                        </button>
                                    </span>
                                </Tooltip>
                            </>
                        ) : null}
                    </div>
                </Grid>
            </Fragment>
        );
    }
}

export default withTranslation()(withStyles(styles)(LodgingV3));
